import SignedInUserDto from "../types/dtos/users/SignedInUserDto";
import { AccessPermissionStringKey } from "../types/access/AccessPermissionStringKey";

type BeaconFn = (method: string, ...args: unknown[]) => void;

declare global {
  interface Window {
    Beacon?: BeaconFn;
    attachEvent?: (event: string, listener: EventListener) => void;
  }
}

export type InAppSupportUserType = "Admin" | "Manager" | "Employee";

const BEACON_SCRIPT_ID = "in-app-support-beacon-script";
const OVERLAY_ID = "in-app-support-overlay";
/** Keep Beacon above the page overlay (matches ModalPopup stacking intent). */
const BEACON_Z_INDEX = 100000;
const OVERLAY_Z_INDEX = 99990;

let scriptLoadPromise: Promise<void> | null = null;
let isInitialised = false;
let areLifecycleEventsBound = false;
let previousBodyOverflow: string | null = null;

function getBeaconId(): string | undefined {
  const value = process.env.REACT_APP_HELPSCOUT_BEACON_ID;
  return value && value.trim().length > 0 ? value.trim() : undefined;
}

function resolveUserType(user: SignedInUserDto): InAppSupportUserType {
  if (user.isAdmin) {
    return "Admin";
  }
  if (user.isManager) {
    return "Manager";
  }
  return "Employee";
}

function hasPlatformConfigurationPermission(user: SignedInUserDto): boolean {
  return (user.permissions as AccessPermissionStringKey[]).some(
    (permission) => permission === "PLATFORM-CONFIGURATION"
  );
}

function showOverlay(): void {
  if (typeof document === "undefined") {
    return;
  }
  if (document.getElementById(OVERLAY_ID)) {
    return;
  }

  const overlay = document.createElement("button");
  overlay.id = OVERLAY_ID;
  overlay.type = "button";
  overlay.setAttribute("aria-label", "Close help");
  Object.assign(overlay.style, {
    position: "fixed",
    inset: "0",
    zIndex: String(OVERLAY_Z_INDEX),
    margin: "0",
    padding: "0",
    border: "none",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    cursor: "pointer",
  });
  overlay.addEventListener("click", () => {
    window.Beacon?.("close");
  });
  document.body.appendChild(overlay);

  previousBodyOverflow = document.body.style.overflow;
  document.body.style.overflow = "hidden";
}

function hideOverlay(): void {
  if (typeof document === "undefined") {
    return;
  }
  document.getElementById(OVERLAY_ID)?.remove();
  if (previousBodyOverflow !== null) {
    document.body.style.overflow = previousBodyOverflow;
    previousBodyOverflow = null;
  }
}

function bindLifecycleEvents(): void {
  if (!window.Beacon || areLifecycleEventsBound) {
    return;
  }
  window.Beacon("on", "open", showOverlay);
  window.Beacon("on", "close", hideOverlay);
  areLifecycleEventsBound = true;
}

function unbindLifecycleEvents(): void {
  if (!window.Beacon || !areLifecycleEventsBound) {
    return;
  }
  window.Beacon("off", "open", showOverlay);
  window.Beacon("off", "close", hideOverlay);
  areLifecycleEventsBound = false;
}

function loadBeaconScript(): Promise<void> {
  if (typeof window === "undefined") {
    return Promise.resolve();
  }

  if (window.Beacon && isInitialised) {
    return Promise.resolve();
  }

  const existing = document.getElementById(BEACON_SCRIPT_ID);
  if (existing && window.Beacon) {
    return Promise.resolve();
  }

  if (scriptLoadPromise) {
    return scriptLoadPromise;
  }

  scriptLoadPromise = new Promise((resolve, reject) => {
    // Official HelpScout Beacon stub so queued calls flush when the script loads.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const stub: any =
      window.Beacon ||
      function (method: string, options?: unknown, data?: unknown) {
        stub.readyQueue.push({ method, options, data });
      };
    stub.readyQueue = stub.readyQueue || [];
    window.Beacon = stub as BeaconFn;

    const script = document.createElement("script");
    script.id = BEACON_SCRIPT_ID;
    script.type = "text/javascript";
    script.async = true;
    script.src = "https://beacon-v2.helpscout.net";
    script.onload = () => resolve();
    script.onerror = () => {
      scriptLoadPromise = null;
      reject(new Error("Failed to load in-app support script"));
    };

    const firstScript = document.getElementsByTagName("script")[0];
    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      document.head.appendChild(script);
    }
  });

  return scriptLoadPromise;
}

const inAppSupportHelper = {
  isConfigured(): boolean {
    return !!getBeaconId();
  },

  shouldShowForUser(user: SignedInUserDto): boolean {
    return (
      this.isConfigured() && hasPlatformConfigurationPermission(user)
    );
  },

  async initialise(user: SignedInUserDto): Promise<void> {
    if (!this.shouldShowForUser(user)) {
      this.shutdown();
      return;
    }

    const beaconId = getBeaconId();
    if (!beaconId) {
      return;
    }

    try {
      await loadBeaconScript();
    } catch {
      return;
    }

    if (!window.Beacon) {
      return;
    }

    const primaryHex = user.client.primaryHexColour?.replace(/^#/, "") || "";
    // `style` must live under `display` — top-level `style` is ignored and the default FAB shows.
    const beaconConfig = {
      display: {
        style: "manual" as const,
        zIndex: BEACON_Z_INDEX,
      },
      ...(primaryHex ? { color: `#${primaryHex}` } : {}),
    };

    if (!isInitialised) {
      // Config before init so the default bottom-right launcher never appears.
      window.Beacon("config", beaconConfig);
      window.Beacon("init", beaconId);
      bindLifecycleEvents();
      isInitialised = true;
    } else {
      window.Beacon("config", beaconConfig);
    }

    window.Beacon("identify", {
      name: user.fullName,
      email: user.email,
      "user-id": user.id,
      "client-id": user.client.id,
      "client-name": user.client.name,
      "is-system-admin": hasPlatformConfigurationPermission(user)
        ? "Yes"
        : "No",
      "is-message-test-user":
        user.firstName === "System" && user.lastName === "Admin"
          ? "Yes"
          : "No",
      "korero-user-type": resolveUserType(user),
    });
  },

  toggle(): void {
    if (!isInitialised || !window.Beacon) {
      return;
    }
    window.Beacon("toggle");
  },

  shutdown(): void {
    hideOverlay();
    unbindLifecycleEvents();
    if (window.Beacon && isInitialised) {
      try {
        window.Beacon("destroy");
      } catch {
        // Ignore destroy errors when Beacon was never fully ready.
      }
    }
    isInitialised = false;
  },
};

export default inAppSupportHelper;
